import type { LoginAnswer } from "@vintecc/capture-api-types/v1";
import RootLayout from "@/layout/RootLayout/RootLayout";
import { useTermsAndPrivacyCheck } from "@/modules/admin/terms-and-privacy/hooks";
import { HeadContent, Navigate, Outlet, useLocation } from "@tanstack/react-router";
import { Navigation } from "@/components/Navigation";
import { Loader } from "@/components/Loader/Loader";
import { Topbar } from "@/components/Topbar";
import { useAppStore } from "@/store/appStore";
import { useEffect, useLayoutEffect } from "react";
import { DialogProvider } from "@vintecc/components";
import { ErrorDisplay } from "@/components/ErrorDisplay/ErrorDisplay";

const isDynamicPathSegment = (segment: string): boolean => {
  return /^(\d+|[0-9a-f]{8}-[0-9a-f-]{27,}|[0-9a-f]{24}|[A-Z0-9]{16,})$/i.test(segment);
};

const normalizePathSegment = (segment: string): string => {
  const normalized = segment.toLowerCase().replace(/[^a-z0-9-]/g, "-");
  return isDynamicPathSegment(normalized) ? "param" : normalized;
};

const buildRouteTestContext = (
  pathname: string,
): {
  routeTestId: string;
  moduleTestId: string;
} => {
  const segments = pathname
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .map(normalizePathSegment);

  const routeSlug = segments.join("-") || "index";
  const moduleSlug = segments[0] || "root";

  return {
    routeTestId: `route-${routeSlug}`,
    moduleTestId: `module-${moduleSlug}`,
  };
};

const RootContent = ({
  loginAnswer,
  isLoading,
  error,
  refreshLoginAnswer,
}: {
  loginAnswer: LoginAnswer | null;
  isLoading: boolean;
  error: string | null;
  refreshLoginAnswer: () => Promise<void>;
}) => {
  const { pathname } = useLocation();
  useTermsAndPrivacyCheck({
    loginAnswer,
    onComplete: refreshLoginAnswer,
  });

  const { routeTestId, moduleTestId } = buildRouteTestContext(pathname);

  // Loading State
  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader size="md" />
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <RootLayout
        top={<></>}
        nav={<></>}
        main={<ErrorDisplay error={{ error: new Error(error) }} />}
        mainTestId={routeTestId}
        moduleTestId={moduleTestId}
      />
    );
  }

  // Unauthenticated State
  if (!loginAnswer) {
    return <Navigate to="/login" />;
  }

  return <RootLayout top={<Topbar />} nav={<Navigation />} main={<Outlet />} mainTestId={routeTestId} moduleTestId={moduleTestId} />;
};

export const RootRoute = ({ brandingStylesheet }: { brandingStylesheet?: string | null }) => {
  const loginAnswer = useAppStore((state) => state.loginAnswer);
  const isLoading = useAppStore((state) => state.isLoading);
  const error = useAppStore((state) => state.error);
  const loadLoginData = useAppStore((state) => state.loadLoginData);
  const refreshLoginAnswer = useAppStore((state) => state.refreshLoginAnswer);

  useLayoutEffect(() => {
    const styleElementId = "branding-stylesheet";
    const existingStyleElement = document.getElementById(styleElementId);

    if (!brandingStylesheet) {
      if (existingStyleElement) {
        existingStyleElement.remove();
      }

      return;
    }

    if (existingStyleElement instanceof HTMLStyleElement) {
      if (existingStyleElement.textContent !== brandingStylesheet) {
        existingStyleElement.textContent = brandingStylesheet;
      }

      return;
    }

    const styleElement = document.createElement("style");
    styleElement.id = styleElementId;
    styleElement.textContent = brandingStylesheet;
    document.head.appendChild(styleElement);
  }, [brandingStylesheet]);

  useEffect(() => {
    loadLoginData();
  }, [loadLoginData]);

  return (
    <>
      <HeadContent />
      <DialogProvider>
        <RootContent loginAnswer={loginAnswer} isLoading={isLoading} error={error} refreshLoginAnswer={refreshLoginAnswer} />
      </DialogProvider>
    </>
  );
};
